if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        // Keep variant changes inside the product section so the page never blanks or fades between shades.
        const shouldFetchFullPage = false;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          this.reorderBrowGelVariantOptions();
        }
      }

      reorderBrowGelVariantOptions() {
        const root = this.variantSelectors;
        if (!root || root.dataset.productHandle !== 'brow-gel') return;

        const shadeOrder = ['Auburn', 'Chocolate', 'Dark Brown', 'Ebony'];
        const rank = (name) => {
          const index = shadeOrder.findIndex(
            (shade) => shade.localeCompare(name.trim(), undefined, { sensitivity: 'base' }) === 0
          );
          return index < 0 ? 999 : index;
        };

        root.querySelectorAll('fieldset').forEach((group) => {
          const labels = [...group.querySelectorAll(':scope > label[for]')];
          const pairs = labels.map((label) => {
            const input = document.getElementById(label.htmlFor);
            return {
              label,
              input,
              name: (input?.value || label.textContent.replace(/Sold out|Unavailable/gi, '')).trim(),
            };
          }).filter((pair) => pair.input);

          pairs.sort((a, b) => rank(a.name) - rank(b.name));
          pairs.forEach(({ input, label }) => {
            group.appendChild(input);
            group.appendChild(label);
          });
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          const selectedVariantLabel = this.querySelector('[data-face-selected-variant-label]');
          if (selectedVariantLabel) selectedVariantLabel.textContent = variant?.title || '';
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // Preserve the shopper's current gallery view across shade changes.
        // Synthetic FACE slides keep stable media IDs while their images update.
        const currentActiveMediaId = this.querySelector(
          'media-gallery .product__media-item.is-active'
        )?.dataset.mediaId;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        const productHandle = this.variantSelectors?.dataset.productHandle;
        const isFaceVariantGallery =
          productHandle === 'soft-matte-concealer' ||
          productHandle === 'brow-gel' ||
          productHandle === 'midnight-seduction-gel-liner' ||
          productHandle === 'pressed-powder-blush' ||
          productHandle === 'ultra-hd-liquid-foundation' ||
          productHandle === 'luxury-silk-hd-finishing-powder' ||
          productHandle === 'icon-illuminator' ||
          productHandle === 'luxury-lip-liner' ||
          productHandle === 'luxury-lip-gloss' ||
          productHandle === 'luxury-liquid-matte-lipstick';

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        if (isFaceVariantGallery && mediaGallerySource && mediaGalleryDestination) {
          const sourceProductImage = mediaGallerySource.querySelector('.face-variant-product-slide img');
          const destinationProductImage = mediaGalleryDestination.querySelector('.face-variant-product-slide img');

          if (sourceProductImage && destinationProductImage) {
            ['src', 'srcset', 'sizes', 'width', 'height', 'alt'].forEach((attribute) => {
              const value = destinationProductImage.getAttribute(attribute);
              if (value === null) {
                sourceProductImage.removeAttribute(attribute);
              } else {
                sourceProductImage.setAttribute(attribute, value);
              }
            });
          }
        }

        // FACE galleries use a stable product-image slot so shade changes update the
        // image source without removing the slide or flashing the gallery to black.
        const activeMediaId = isFaceVariantGallery
          ? `${this.dataset.section}-face-variant-product`
          : `${this.dataset.section}-${variantFeaturedMediaId}`;
        const activeMediaItem = this.querySelector(`media-gallery [data-media-id="${activeMediaId}"]`);
        if (activeMediaItem && !isFaceVariantGallery) {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(activeMediaId, false);
        }

        // Update FACE's synthetic shade-swipe slides and thumbnails from the freshly rendered section.
        const sourceFaceSwipes = mediaGallerySource?.querySelectorAll('.face-shade-swipe-slide img') ?? [];
        const destinationFaceSwipes =
          mediaGalleryDestination?.querySelectorAll('.face-shade-swipe-slide img') ?? [];
        sourceFaceSwipes.forEach((sourceImage, index) => {
          const destinationImage = destinationFaceSwipes[index];
          if (!destinationImage) return;
          sourceImage.src = destinationImage.src;
          sourceImage.alt = destinationImage.alt;
        });

        const sourceFaceThumbnails = this.querySelectorAll('.face-shade-swipe-thumbnail');
        const destinationFaceThumbnails = html.querySelectorAll('.face-shade-swipe-thumbnail');
        sourceFaceThumbnails.forEach((sourceThumbnail, index) => {
          const destinationThumbnail = destinationFaceThumbnails[index];
          if (!destinationThumbnail) return;

          sourceThumbnail.dataset.target = destinationThumbnail.dataset.target;
          const sourceButton = sourceThumbnail.querySelector('button');
          const destinationButton = destinationThumbnail.querySelector('button');
          if (sourceButton && destinationButton) {
            sourceButton.dataset.mediaId = destinationButton.dataset.mediaId;
            sourceButton.setAttribute('aria-label', destinationButton.getAttribute('aria-label'));
          }

          const sourceImage = sourceThumbnail.querySelector('img');
          const destinationImage = destinationThumbnail.querySelector('img');
          if (sourceImage && destinationImage) {
            sourceImage.src = destinationImage.src;
            sourceImage.alt = destinationImage.alt;
          }
        });

        const sourceGalleryThumbItems = this.querySelectorAll(
          'media-gallery [id^="Slider-Thumbnails"] > li[data-target]'
        );
        const destinationGalleryThumbItems = html.querySelectorAll(
          'media-gallery [id^="Slider-Thumbnails"] > li[data-target]'
        );

        sourceGalleryThumbItems.forEach((sourceThumbnail, index) => {
          const destinationThumbnail = destinationGalleryThumbItems[index];
          if (!destinationThumbnail) return;

          sourceThumbnail.dataset.target = destinationThumbnail.dataset.target;
          sourceThumbnail.dataset.mediaPosition = destinationThumbnail.dataset.mediaPosition;

          const sourceButton = sourceThumbnail.querySelector('button');
          const destinationButton = destinationThumbnail.querySelector('button');
          if (sourceButton && destinationButton) {
            ['aria-label', 'aria-current', 'aria-describedby', 'data-media-id'].forEach((attribute) => {
              const value = destinationButton.getAttribute(attribute);
              if (value === null) {
                sourceButton.removeAttribute(attribute);
              } else {
                sourceButton.setAttribute(attribute, value);
              }
            });
          }

          const sourceImage = sourceThumbnail.querySelector('img');
          const destinationImage = destinationThumbnail.querySelector('img');
          if (sourceImage && destinationImage) {
            sourceImage.src = destinationImage.src;
            sourceImage.alt = destinationImage.alt;
          }
        });

        if (activeMediaItem && isFaceVariantGallery) {
          const preservedMediaItem = currentActiveMediaId
            ? this.querySelector(`media-gallery [data-media-id="${currentActiveMediaId}"]`)
            : null;
          const nextActiveMediaId = preservedMediaItem ? currentActiveMediaId : activeMediaId;
          this.querySelector('media-gallery')?.setActiveMedia?.(nextActiveMediaId, false);
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}


/* FACE variant-specific swipe synchronizer */
if (!window.faceVariantSwipeSyncBound) {
  window.faceVariantSwipeSyncBound = true;

  subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { html, variant } }) => {
    if (!html || !variant) return;

    document.querySelectorAll('product-info').forEach((productInfo) => {
      const variantInput = productInfo.querySelector('input[name="id"]');
      if (!variantInput || String(variantInput.value) !== String(variant.id)) return;

      const freshSwipe = html.querySelector('media-gallery .face-shade-swipe-slide img');
      const currentSwipe = productInfo.querySelector('media-gallery .face-shade-swipe-slide img');
      if (freshSwipe && currentSwipe) {
        currentSwipe.src = freshSwipe.src;
        currentSwipe.alt = freshSwipe.alt;
      }

      const freshThumb = html.querySelector('media-gallery .face-shade-swipe-thumbnail');
      const currentThumb = productInfo.querySelector('media-gallery .face-shade-swipe-thumbnail');
      if (freshThumb && currentThumb) {
        currentThumb.dataset.target = freshThumb.dataset.target;

        const freshButton = freshThumb.querySelector('button');
        const currentButton = currentThumb.querySelector('button');
        if (freshButton && currentButton) {
          currentButton.dataset.mediaId = freshButton.dataset.mediaId;
          currentButton.setAttribute('aria-label', freshButton.getAttribute('aria-label'));
        }

        const freshThumbImage = freshThumb.querySelector('img');
        const currentThumbImage = currentThumb.querySelector('img');
        if (freshThumbImage && currentThumbImage) {
          currentThumbImage.src = freshThumbImage.src;
          currentThumbImage.alt = freshThumbImage.alt;
        }
      }
    });
  });
}

